import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import type { ApiResponse, AuthSession } from '@sparqplug/types';

export function LoginPage() {
  const navigate = useNavigate();
  const { user, isLoading, setUser, setOrganization, setAccessToken } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user && !isLoading) navigate('/dashboard', { replace: true });
  }, [isLoading, navigate, user]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api.post<ApiResponse<AuthSession>>('/auth/login', { email: email.trim(), password });
      if (!response.success || !response.data) throw new Error('Sign-in failed.');
      setAccessToken(response.data.accessToken);
      setUser(response.data.user);
      setOrganization(response.data.organization);
      navigate('/dashboard', { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Sign-in failed.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="rounded-2xl border border-border/80 bg-card/70 p-5 shadow-sm backdrop-blur sm:p-6">
      <h1 className="mb-1 text-2xl font-bold text-foreground">Welcome back</h1>
      <p className="mb-8 text-sm text-muted-foreground">Sign in to your SparQPlug account</p>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <div role="alert" className="rounded-md border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
        <div className="space-y-1.5"><label htmlFor="email" className="text-sm font-medium text-foreground">Email</label><input id="email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required autoComplete="email" className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" placeholder="you@company.com" /></div>
        <div className="space-y-1.5"><label htmlFor="password" className="text-sm font-medium text-foreground">Password</label><div className="relative"><input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(event) => setPassword(event.target.value)} required autoComplete="current-password" className="flex h-9 w-full rounded-md border border-input bg-background px-3 pr-10 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" placeholder="••••••••" /><button type="button" onClick={() => setShowPassword((value) => !value)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={showPassword ? 'Hide password' : 'Show password'}>{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button></div></div>
        <button type="submit" disabled={loading || isLoading} className="inline-flex w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50">{loading || isLoading ? 'Signing in…' : 'Sign in'}</button>
      </form>
      <p className="mt-6 text-center text-sm text-muted-foreground">Don't have an account? <Link to="/auth/register" className="font-medium text-primary hover:underline">Create one</Link></p>
    </div>
  );
}
